import type { JssStyle } from 'jss';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { ShopCartWidgetData } from '@web/widgets/shop-cart/type';
import type { MiniCartSettings } from '@common/types/mini-cart-settings.type';
import type { FormButtonSettings } from '@components/sp-form-button-settings';

import { getBaseFormStyles } from '@web/widgets/common/base/base-form/base-form.styles';
import { getBaseButtonData } from '@web/widgets/common/base/base-button/base-button.styles';
import { EButtonStyleType, ECartViewType, EFontStyle, EHorizontalPosition, ESize, Widgets } from '@common/enums';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

import { themeColorCss, themeFontCss } from '@sites/site-theme/src/utils/variables';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';

import { counterSizeStyles } from '@web/widgets/product-card/components/product-card-view/product-card-view.component.styles';
import { ThemeColorNames } from '@sites/site-theme/src/enums/color-names.enum';

function previewSize(size: ESize): string {
    switch (size) {
        case ESize.S:
            return '80px';
        case ESize.M:
            return '120px';
        case ESize.L:
            return '160px';
        default:
            return '80px';
    }
}

function previewIconSize(size: ESize): string {
    switch (size) {
        case ESize.S:
            return '40px';
        case ESize.M:
            return '48px';
        case ESize.L:
            return '56px';
        default:
            return '48px';
    }
}

function getFontStyles(fontStyle: EFontStyle[]): JssStyle {
    return {
        fontWeight: fontStyle.includes(EFontStyle.B) ? 'bold' : 'initial',
        fontStyle: fontStyle.includes(EFontStyle.I) ? 'italic' : 'initial',
        textTransform: fontStyle.includes(EFontStyle.AT) ? 'uppercase' : 'initial',
    };
}

export function getMiniCartStyles(miniCartSet: MiniCartSettings): JssStyle {
    return {
        width: '360px',
        background: themeColorCss(miniCartSet.modalBackgroundColor),
        borderRadius: `${miniCartSet.modalBorderRadius}px`,
        boxShadow: '0 4px 16px rgba(51, 51, 51, 0.12)',
        overflow: 'hidden',
        fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
        color: themeColorCss(miniCartSet.textColor),
        '& .mini-cart-header': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: `1px solid ${themeColorCss(miniCartSet.separatorColor)}`,
            '& .mini-cart-title': {
                fontWeight: 700,
                fontSize: '16px',
                color: themeColorCss(miniCartSet.textColor),
            },
            '& .mini-cart-close': {
                cursor: 'pointer',
                color: themeColorCss(miniCartSet.textColor),
                fontSize: '18px',
                lineHeight: '18px',
                background: 'transparent',
                border: 'none',
            },
        },
        '& .mini-cart-items': {
            padding: '4px 20px',
            maxHeight: 'calc(100vh - 300px)',
            height: 'auto',
            overflowY: 'scroll',
        },
        '& .mini-cart-item': {
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 0',
            borderBottom: `1px solid ${themeColorCss(miniCartSet.separatorColor)}`,
            '&:last-child': {
                borderBottom: 'none',
            },
            '& .mini-cart-item-photo': {
                flexShrink: 0,
                width: '64px',
                height: '64px',
                borderRadius: '4px',
                overflow: 'hidden',
                background: '#eee',
                cursor: 'pointer',
                '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                },
            },
            '& .mini-cart-item-body': {
                flex: 1,
                minWidth: 0,
                paddingRight: '26px',
                '& .mini-cart-item-title': {
                    fontWeight: 700,
                    fontSize: '14px',
                    marginBottom: '2px',
                    color: themeColorCss(miniCartSet.textColor),
                    cursor: 'pointer',
                },
                '& .mini-cart-item-property': {
                    fontSize: '12px',
                    marginBottom: '8px',
                    opacity: 0.7,
                    color: themeColorCss(miniCartSet.textColor),
                },
                '& .mini-cart-item-controls': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '10px',
                },
                '& .mini-cart-counter': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: themeColorCss(miniCartSet.countButtonBorderColor),
                    background: themeColorCss(miniCartSet.countButtonBgColor),
                    color: themeColorCss(miniCartSet.countButtonTextColor),
                    borderRadius: `${miniCartSet.countButtonRounding}px`,
                    overflow: 'hidden',
                    '& button': {
                        background: 'transparent',
                        border: 'none',
                        color: themeColorCss(miniCartSet.countButtonTextColor),
                        width: '22px',
                        height: '22px',
                        fontSize: '14px',
                        lineHeight: '22px',
                        cursor: 'pointer',
                        padding: 0,
                        '&:hover': { opacity: 0.8 },
                    },
                    '& .mini-cart-counter-value': {
                        width: '20px',
                        padding: 0,
                        textAlign: 'center',
                        fontSize: '13px',
                        color: themeColorCss(miniCartSet.countButtonTextColor),
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        MozAppearance: 'textfield',
                        appearance: 'textfield',
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                    },
                },
                '& .mini-cart-prices': {
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    columnGap: '6px',
                    flexWrap: 'wrap',
                    '& .mini-cart-price': {
                        fontWeight: 700,
                        fontSize: '12px',
                        color: themeColorCss(miniCartSet.priceColor),
                    },
                    '& .mini-cart-old-price': {
                        fontSize: '12px',
                        textDecoration: 'line-through',
                        color: themeColorCss(miniCartSet.oldPriceColor),
                    },
                },
            },
            '& .mini-cart-remove': {
                position: 'absolute',
                top: '32px',
                right: 0,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                lineHeight: '20px',
                cursor: 'pointer',
                border: 'none',
                background: themeColorCss(miniCartSet.deleteButtonBgColor),
                color: themeColorCss(miniCartSet.deleteButtonIconColor),
                '&:hover': { opacity: 0.8 },
            },
        },
        '& .mini-cart-footer': {
            padding: '16px 20px 20px',
            borderTop: `1px solid ${themeColorCss(miniCartSet.separatorColor)}`,
            '& .mini-cart-total': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: '14px',
                '& .mini-cart-total-label': {
                    fontWeight: 400,
                    fontSize: '16px',
                    color: themeColorCss(miniCartSet.textColor),
                },
                '& .mini-cart-total-value': {
                    fontWeight: 700,
                    fontSize: '16px',
                    color: themeColorCss(miniCartSet.priceColor),
                },
            },
        },
        '& .mini-cart-empty': {
            padding: '0px 20px 30px 20px',
            textAlign: 'center',
        },
    };
}

export function getMiniCartButtonJssStyles(buttonSettings?: FormButtonSettings): {
    miniCartButtonStyles: JssStyle;
    miniCartButtonWrapper: JssStyle;
} {
    let miniCartButtonStyles: JssStyle = {
        boxSizing: 'border-box',
        width: '100%',
        flexDirection: buttonSettings?.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
    };

    if (buttonSettings?.styleType === EButtonStyleType.custom) {
        miniCartButtonStyles = {
            ...miniCartButtonStyles,
            ...getBaseButtonData({
                buttonBase: buttonSettings.buttonBase,
                buttonHover: buttonSettings.buttonHover,
                advancedRadius: buttonSettings.advancedRadius,
                advancedBorderSettings: buttonSettings.advancedBorderSettings,
            }),
        };
    }

    if (buttonSettings?.isShadow) {
        const s = buttonSettings?.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        miniCartButtonStyles = {
            ...miniCartButtonStyles,
            boxShadow: `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`,
        };
    }

    return {
        miniCartButtonStyles,
        miniCartButtonWrapper: { display: 'flex' },
    };
}

export function getStyles(
    { data }: WebLegacyWidgetEntity<ShopCartWidgetData>,
    { isConstructor }: { isConstructor: boolean }
): JssStyle {
    const cartSet = data.cartSettings;
    const cartIconSet = data.cartIconSettings;
    const cartViewType: JssStyle = {
        position: 'relative',
        display: 'flex',
        flexDirection: data.cartViewType === ECartViewType.HORIZONTAL ? 'row' : 'column',
        gap: '30px',
        '& .cart-items': {
            width: data.cartViewType === ECartViewType.HORIZONTAL ? 'calc(60% - 30px)' : '100%',
        },
        '& .cart-form-wrapper': {
            width: data.cartViewType === ECartViewType.HORIZONTAL ? '40%' : '100%',
            height: 'fit-content',
        },
        '@media (max-width: 1200px)': {
            flexDirection: 'column',
            '& .cart-items': {
                width: '100%',
            },
            '& .cart-form-wrapper': {
                width: '100%',
                '& .cart-form': {
                    margin: '0 auto',
                },
            },
        },
        '&.mobileViewCart': {
            flexDirection: 'column',
            '& .cart-items': {
                width: '100%',
            },
            '& .cart-form-wrapper': {
                width: '100%',
                '& .cart-form': {
                    margin: '0 auto',
                },
            },
        },
    };
    const cartStyle: JssStyle = {
        '& .item': {
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '20px 50px 20px 0',
            borderBottom: cartSet.isSeparatedItems
                ? `${cartSet.separatorWidth}px ${cartSet.separatorType} ${themeColorCss(
                      cartSet.separatorColor as string
                  )}`
                : 'none',
            '& .modal-divider': {
                display: 'block',
                width: '100%',
                height: '1px',
                margin: '25px 0',
                background: themeColorCss(ThemeColorNames.primary),
            },
            '& .item-info': {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                '& .item-info-photo': {
                    background: '#eee',
                    marginRight: '20px',
                    '& img': {
                        width: previewSize(cartSet.previewSize),
                        height: previewSize(cartSet.previewSize),
                        objectFit: 'cover',
                    },
                },
                '& .item-info-text': {
                    marginRight: '20px',
                    '& h3': {
                        ...getFontStyles(cartSet.itemTitleFontStyle),
                        fontSize: `${cartSet.itemTitleFontSize}px`,
                        color: cartSet.itemTitleFontColor,
                        cursor: 'pointer',
                        marginBottom: '20px',
                        marginTop: '10px',
                        fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                    },
                    '& .parameters': {
                        '& label': {
                            fontSize: `${cartSet.itemPropertyFontSize + 2}px`,
                            fontWeight: 'bold',
                            marginBottom: '10px',
                            fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                        },
                        '& p': {
                            ...getFontStyles(cartSet.itemPropertyFontStyle),
                            fontSize: `${cartSet.itemPropertyFontSize}px`,
                            color: cartSet.itemPropertyFontColor,
                            marginBottom: '5px',
                            fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                            '& .param-prop': {
                                marginRight: '5px',
                            },
                            '& .param-value': {
                                opacity: 0.7,
                            },
                        },
                    },
                },
            },
            '& .item-price': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'end',
                gap: '10px',
                justifyContent: 'flex-start',
                marginTop: '10px',
                '& .card-product-counter': {
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    borderWidth: '1px',
                    boxSizing: 'border-box',
                    borderStyle: 'solid',
                    whiteSpace: 'nowrap',
                    borderRadius: `${cartSet.countButtonRounding}px`,
                    background: themeColorCss(cartSet.countButtonBgColor as string),
                    borderColor: themeColorCss(cartSet.countButtonBorderColor as string),
                    color: themeColorCss(cartSet.countButtonTextColor as string),
                    ...counterSizeStyles[cartSet.countButtonSize || ESize.M],
                    padding: '0 !important',
                    '& .counter-control': {
                        cursor: 'pointer',
                        padding: counterSizeStyles[cartSet.countButtonSize || ESize.M].padding,
                        background: 'transparent',
                        border: 'none',
                        color: themeColorCss(cartSet.countButtonTextColor as string),
                        '&:hover': {
                            opacity: 0.8,
                        },
                    },
                    '& .quantity-control': {
                        background: 'transparent',
                        color: themeColorCss(cartSet.countButtonTextColor as string),
                        fontSize: counterSizeStyles[(cartSet as any).inputSize || ESize.M].fontSize,
                        lineHeight: counterSizeStyles[(cartSet as any).inputSize || ESize.M].lineHeight,
                    },
                    '& .disabled': {
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                        opacity: 0.5,
                    },
                },
                '& .price': {
                    ...getFontStyles(cartSet.itemPriceFontStyle),
                    fontSize: `${cartSet.itemPriceFontSize}px`,
                    color: cartSet.itemPriceFontColor,
                    fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                },
            },
            '& .close-icon': {
                position: 'absolute',
                top: 'calc(50% - 12px)',
                right: 0,
                background: themeColorCss(cartSet.deleteButtonBgColor),
                color: themeColorCss(cartSet.deleteButtonIconColor),
                width: '24px',
                height: '24px',
                textAlign: 'center',
                fontSize: '22px',
                lineHeight: '24px',
                borderRadius: '50%',
                fontWeight: 'lighter',
                fontFamily: 'inherit',
                alignSelf: 'center',
                '&:hover': {
                    opacity: 0.8,
                    cursor: 'pointer',
                },
            },
            '@media (max-width: 768px)': {
                flexDirection: 'column',
                gap: '20px',
                paddingBottom: '20px',
                '& .item-info': {
                    flexDirection: 'column',
                    '& .item-info-photo': {
                        marginBottom: '20px',
                    },
                },
                '& .item-price': {
                    justifyContent: 'start',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '20px',
                    marginTop: '0',
                },
                '& .close-icon': {
                    top: '20px',
                },
            },
            '&.mobileViewCartItem': {
                flexDirection: 'column',
                gap: '20px',
                paddingBottom: '20px',
                '& .item-info': {
                    flexDirection: 'column',
                    '& .item-info-photo': {
                        marginBottom: '20px',
                    },
                },
                '& .item-price': {
                    justifyContent: 'start',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '20px',
                    marginTop: '0',
                },
                '& .close-icon': {
                    top: '20px',
                },
            },
        },
        '& .cart-form-wrapper': {
            width: data.cartViewType === ECartViewType.HORIZONTAL ? '45%' : '100%',
        },
        '& .total-price': {
            marginTop: '30px',
            display: 'inline-flex',
            alignItems: 'baseline',

            '& p': {
                ...getFontStyles(cartSet.itemTotalPriceFontStyle),
                fontSize: `${cartSet.itemTotalPriceFontSize}px`,
                color: cartSet.itemTotalPriceFontColor,
                marginRight: '10px',
            },

            '& span': {
                fontSize: '18px',
                fontWeight: 400,
                textDecoration: 'line-through',
                color: '#D4696A',
                marginRight: '5px',
            },
        },
    };
    const cartIconStyle: JssStyle = {
        position: 'fixed',
        right: '3%',
        top: '3%',
        '& .cart-icon-wrapper': {
            borderRadius: `${cartIconSet.iconRounding}px`,
            background: cartIconSet.iconBgColor,
            color: cartIconSet.iconColor,
            height: previewIconSize(cartIconSet.iconSize),
            width: previewIconSize(cartIconSet.iconSize),
        },
        '& .icon-counter': {
            background: cartIconSet.iconCounterBgColor,
            color: cartIconSet.iconCounterColor,
        },
    };

    const miniCartStyle: JssStyle = data.miniCartSettings ? getMiniCartStyles(data.miniCartSettings) : {};

    const { miniCartButtonStyles, miniCartButtonWrapper } = getMiniCartButtonJssStyles(
        data.miniCartSettings?.buttonSettings
    );

    const cartFormWrapper: JssStyle = {
        background: themeColorCss(data.inputSettings.modalBackgroundColor),
        padding: data.inputSettings.modalPadding + 'px',
        borderRadius: data.inputSettings.modalBorderRadius + 'px',
    };

    const formDropdownButton = {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '39px',
        backgroundColor: '#fff',
        border: '1px solid #bcc8c8',
        boxSizing: 'border-box',
        borderRadius: '4px',

        '& div': {
            display: 'flex',
            alignItems: 'center',
        },

        '& span': {
            fontWeight: 'normal',
        },

        '& img': {
            width: '20px',
            height: '20px',
            marginRight: '10px',
        },

        '&:hover': {
            borderColor: '#b8cece',
            backgroundColor: '#fff',
        },

        '& .open': {
            '& .dropdown-toggle.custom-btn': {
                backgroundColor: '#fff',
                borderColor: '#00b175',
                outline: '0 !important',
                boxShadow: '0 0 0 2px rgba(49,193,120,0.2)',
            },

            '& .dropdown-menu': {
                margin: '0 5px',
                maxWidth: '300px',
            },
        },
    };

    const cartEmptyState: JssStyle = {
        width: '100%',
        padding: '20px',
        color: cartSet.itemTitleFontColor,
        textAlign: 'center',
    };

    const warningMsg: JssStyle = {
        width: '100%',
        backgroundColor: '#F5EDDB',
        padding: '20px 18px',
    };

    return {
        ...getBaseFormStyles(data, Widgets.SHOP_CART, isConstructor),
        cartIconStyle,
        cartViewType,
        cartStyle,
        cartFormWrapper,
        cartEmptyState,
        formDropdownButton,
        warningMsg,
        miniCartStyle,
        miniCartButtonStyles,
        miniCartButtonWrapper,
    };
}
